import { Event, EventStatus } from '@/types/event';

/**
 * Parse date string từ format DD/MM/YYYY thành Date object
 */
export function parseEventDate(dateStr: string): Date | null {
  const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = dateStr.match(regex);

  if (!match) {
    console.warn(`Invalid date format: ${dateStr}. Expected DD/MM/YYYY`);
    return null;
  }

  const [, day, month, year] = match;
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  // Kiểm tra tính hợp lệ của date
  if (
    date.getFullYear() !== parseInt(year) ||
    date.getMonth() !== parseInt(month) - 1 ||
    date.getDate() !== parseInt(day)
  ) {
    console.warn(`Invalid date: ${dateStr}`);
    return null;
  }

  return date;
}

/**
 * Parse time string từ format "HH:mm - HH:mm" thành object {start, end}
 */
export function parseEventTime(
  timeStr: string
): { start: { hour: number; minute: number }; end: { hour: number; minute: number } } | null {
  const regex = /^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/;
  const match = timeStr.match(regex);

  if (!match) {
    console.warn(`Invalid time format: ${timeStr}. Expected HH:mm - HH:mm`);
    return null;
  }

  const [, startHour, startMinute, endHour, endMinute] = match;

  const start = {
    hour: parseInt(startHour),
    minute: parseInt(startMinute),
  };

  const end = {
    hour: parseInt(endHour),
    minute: parseInt(endMinute),
  };

  // Validate time values
  if (
    start.hour < 0 ||
    start.hour > 23 ||
    start.minute < 0 ||
    start.minute > 59 ||
    end.hour < 0 ||
    end.hour > 23 ||
    end.minute < 0 ||
    end.minute > 59
  ) {
    console.warn(`Invalid time values: ${timeStr}`);
    return null;
  }

  return { start, end };
}

/**
 * Tạo Date object từ event date và time
 */
export function createEventDateTime(
  dateStr: string,
  timeStr: string,
  isEndTime: boolean = false
): Date | null {
  const eventDate = parseEventDate(dateStr);
  const eventTime = parseEventTime(timeStr);

  if (!eventDate || !eventTime) {
    return null;
  }

  const time = isEndTime ? eventTime.end : eventTime.start;

  const dateTime = new Date(eventDate);
  dateTime.setHours(time.hour, time.minute, 0, 0);

  return dateTime;
}

/**
 * Tính toán status của event dựa trên thời gian hiện tại
 */
export function calculateEventStatus(event: Event): EventStatus {
  const now = new Date();

  const startDateTime = createEventDateTime(event.date, event.time, false);
  const endDateTime = createEventDateTime(event.date, event.time, true);

  if (!startDateTime || !endDateTime) {
    console.warn(`Invalid date/time format for event: ${event.title}`);
    return EventStatus.UPCOMING; // Default fallback
  }

  // Tính toán thời gian 1 ngày trước start time
  const oneDayBeforeStart = new Date(startDateTime);
  oneDayBeforeStart.setDate(oneDayBeforeStart.getDate() - 1);

  // Logic status:
  // - upcoming: trước 1 ngày so với start time
  // - living: giữa start time và end time
  // - ended: sau end time

  if (now < oneDayBeforeStart) {
    return EventStatus.UPCOMING;
  } else if (now >= startDateTime && now <= endDateTime) {
    return EventStatus.LIVING;
  } else if (now > endDateTime) {
    return EventStatus.ENDED;
  } else {
    // Trong khoảng 1 ngày trước start time đến start time
    return EventStatus.UPCOMING;
  }
}

/**
 * Cập nhật status cho tất cả events
 */
export function updateEventsStatus(events: Event[]): Event[] {
  return events.map((event) => ({
    ...event,
    status: calculateEventStatus(event),
  }));
}

/**
 * Validate format của date và time trong event
 */
export function validateEventDateTime(event: Event): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Validate date format
  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  if (!dateRegex.test(event.date)) {
    errors.push(`Invalid date format: "${event.date}". Expected DD/MM/YYYY`);
  } else {
    const parsedDate = parseEventDate(event.date);
    if (!parsedDate) {
      errors.push(`Invalid date value: "${event.date}"`);
    }
  }

  // Validate time format
  const timeRegex = /^\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}$/;
  if (!timeRegex.test(event.time)) {
    errors.push(`Invalid time format: "${event.time}". Expected HH:mm - HH:mm`);
  } else {
    const parsedTime = parseEventTime(event.time);
    if (!parsedTime) {
      errors.push(`Invalid time value: "${event.time}"`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate tất cả events và in ra lỗi nếu có
 */
export function validateAllEvents(events: Event[]): void {
  const invalidEvents: { event: Event; errors: string[] }[] = [];

  events.forEach((event) => {
    const validation = validateEventDateTime(event);
    if (!validation.isValid) {
      invalidEvents.push({
        event,
        errors: validation.errors,
      });
    }
  });

  if (invalidEvents.length > 0) {
    console.warn('Found invalid events:');
    invalidEvents.forEach(({ event, errors }) => {
      console.warn(`Event "${event.title}" (ID: ${event.id}):`);
      errors.forEach((error) => console.warn(`  - ${error}`));
    });
  } else {
    console.log('All events have valid date/time format');
  }
}
